import { UnitClass, UnitType } from '../types/military.js';
/**
 * Unit catalogue, verbatim from the GDD except where noted.
 *
 * SIEGE TRAINING BUILDING - the GDD gates Rams and Catapults behind
 * "Smithy L8: Siege Workshop" but never defines a Workshop building (no cost,
 * no level curve), even though the art pack includes one. Siege is therefore
 * trained at the Smithy itself. When a Workshop is specified, change
 * `trainedAt` here and nothing else needs to move.
 */
export const UNITS = {
    spearman: {
        type: UnitType.SPEARMAN,
        name: 'Spearman',
        role: 'Anti-cavalry / defensive infantry',
        unitClass: UnitClass.INFANTRY,
        attack: 10,
        defInfantry: 15,
        defCavalry: 45,
        defArchers: 20,
        speed: 18,
        carryCapacity: 25,
        population: 1,
        cost: { wood: 50, clay: 30, iron: 20, grain: 10 },
        trainingTimeSeconds: 120,
        trainedAt: 'barracks',
        requires: { barracks: 1 },
    },
    swordsman: {
        type: UnitType.SWORDSMAN,
        name: 'Swordsman',
        role: 'Anti-infantry / defensive infantry',
        unitClass: UnitClass.INFANTRY,
        attack: 25,
        defInfantry: 50,
        defCavalry: 25,
        defArchers: 40,
        speed: 22,
        carryCapacity: 15,
        population: 1,
        /*
         * Iron-heavy on purpose: a sword is the one early weapon a smith makes.
         *
         * It cost 40/50/30 like everything else, and with the rest of tier one
         * leaning on wood, iron had no buyer until crossbows and cavalry - a
         * mine you build in week one for a payoff in week three. The swordsman
         * is the classic early iron sink (Tribal Wars prices its swordsman at
         * 30/30/70 for the same reason), and pricing him that way gives the
         * iron mine a customer from the first barracks upgrade.
         */
        cost: { wood: 30, clay: 30, iron: 70, grain: 15 },
        trainingTimeSeconds: 150,
        trainedAt: 'barracks',
        requires: { barracks: 3 },
    },
    axeman: {
        type: UnitType.AXEMAN,
        name: 'Axeman',
        role: 'Primary offensive infantry',
        unitClass: UnitClass.INFANTRY,
        attack: 45,
        defInfantry: 10,
        defCavalry: 5,
        defArchers: 10,
        speed: 18,
        carryCapacity: 10,
        population: 1,
        cost: { wood: 60, clay: 30, iron: 40, grain: 15 },
        trainingTimeSeconds: 180,
        trainedAt: 'barracks',
        requires: { barracks: 5 },
    },
    /**
     * The village levy: townsfolk handed a bow.
     *
     * Exists to fill the gap before the Archer, which needs Barracks 8. Until
     * then a new player has no ranged defence at all and no answer to a spear
     * wall, so the early game had one viable defensive shape. Cheap, weak, and
     * available almost immediately.
     */
    militia_archer: {
        type: UnitType.MILITIA_ARCHER,
        name: 'Militia Archer',
        role: 'Early ranged defence',
        unitClass: UnitClass.ARCHER,
        attack: 8,
        defInfantry: 25,
        defCavalry: 20,
        defArchers: 12,
        speed: 20,
        carryCapacity: 5,
        population: 1,
        cost: { wood: 40, clay: 15, iron: 5, grain: 10 },
        trainingTimeSeconds: 75,
        trainedAt: 'barracks',
        requires: { barracks: 2 },
    },
    archer: {
        type: UnitType.ARCHER,
        name: 'Archer',
        role: 'Ranged defence / support',
        unitClass: UnitClass.ARCHER,
        attack: 15,
        defInfantry: 50,
        defCavalry: 40,
        /*
         * Was 5, a tenth of its anti-infantry value. That is the widest single
         * spread in the game and it made archer-versus-archer a blowout: an
         * attacking archer stack broke a defending one for barely half its cost,
         * where every other matchup sits near or above parity. 15 keeps archers
         * clearly the wrong answer to archers without making the counter absolute.
         */
        defArchers: 15,
        speed: 18,
        carryCapacity: 10,
        population: 1,
        cost: { wood: 80, clay: 20, iron: 20, grain: 15 },
        trainingTimeSeconds: 160,
        trainedAt: 'barracks',
        requires: { barracks: 8 },
    },
    /**
     * Armour-piercing ranged offence, gated behind the Smithy.
     *
     * Named in the design brief as a Smithy research unlock. It fills a hole in
     * the roster rather than upgrading the Archer: before it, ranged offence did
     * not exist. The Archer costs 135 for 15 attack, so it can only ever sit at
     * home, and every offensive option was infantry or horse.
     *
     * Its counter row is the point. Nothing else cheap threatens plate, so the
     * 1.6 into Heavy Cavalry and 1.7 into Knights are what buy it a place. A
     * first pass gave it 40 attack, which made the matrix bonus multiply a base
     * too small to matter: it came out a generalist with no bad matchup and no
     * good one. Offence had to come up for the counter to mean anything.
     */
    crossbowman: {
        type: UnitType.CROSSBOWMAN,
        name: 'Crossbowman',
        role: 'Armour-piercing ranged offence',
        unitClass: UnitClass.ARCHER,
        attack: 62,
        defInfantry: 48,
        defCavalry: 55,
        defArchers: 22,
        speed: 21,
        carryCapacity: 10,
        population: 2,
        cost: { wood: 110, clay: 40, iron: 90, grain: 25 },
        trainingTimeSeconds: 260,
        trainedAt: 'barracks',
        requires: { barracks: 10, smithy: 15 },
    },
    scout: {
        type: UnitType.SCOUT,
        name: 'Scout',
        role: 'Reconnaissance',
        unitClass: UnitClass.CAVALRY,
        attack: 0,
        defInfantry: 2,
        defCavalry: 1,
        defArchers: 2,
        speed: 9,
        carryCapacity: 0,
        population: 1,
        cost: { wood: 30, clay: 30, iron: 20, grain: 10 },
        trainingTimeSeconds: 90,
        trainedAt: 'stable',
        requires: { stable: 1 },
    },
    light_cavalry: {
        type: UnitType.LIGHT_CAVALRY,
        name: 'Light Cavalry',
        role: 'Fast offence / raiding',
        unitClass: UnitClass.CAVALRY,
        attack: 130,
        defInfantry: 30,
        defCavalry: 40,
        defArchers: 30,
        speed: 10,
        carryCapacity: 80,
        population: 2,
        cost: { wood: 100, clay: 80, iron: 120, grain: 50 },
        trainingTimeSeconds: 240,
        trainedAt: 'stable',
        requires: { stable: 3 },
    },
    heavy_cavalry: {
        type: UnitType.HEAVY_CAVALRY,
        name: 'Heavy Cavalry',
        role: 'Elite general offence / defence',
        unitClass: UnitClass.CAVALRY,
        attack: 150,
        defInfantry: 200,
        defCavalry: 160,
        defArchers: 180,
        speed: 11,
        carryCapacity: 50,
        population: 3,
        cost: { wood: 150, clay: 120, iron: 200, grain: 80 },
        trainingTimeSeconds: 300,
        trainedAt: 'stable',
        requires: { stable: 8, smithy: 5 },
    },
    ram: {
        type: UnitType.RAM,
        name: 'Battering Ram',
        role: 'Siege - wall destruction',
        unitClass: UnitClass.SIEGE,
        attack: 2,
        defInfantry: 20,
        defCavalry: 50,
        defArchers: 20,
        speed: 30,
        carryCapacity: 0,
        population: 4,
        cost: { wood: 300, clay: 150, iron: 250, grain: 50 },
        trainingTimeSeconds: 600,
        trainedAt: 'smithy',
        requires: { smithy: 8 },
    },
    catapult: {
        type: UnitType.CATAPULT,
        name: 'Catapult',
        role: 'Siege - building destruction',
        unitClass: UnitClass.SIEGE,
        attack: 100,
        defInfantry: 100,
        defCavalry: 50,
        defArchers: 100,
        speed: 30,
        carryCapacity: 0,
        population: 5,
        cost: { wood: 400, clay: 200, iron: 400, grain: 100 },
        trainingTimeSeconds: 900,
        trainedAt: 'smithy',
        requires: { smithy: 10 },
    },
    bailiff: {
        type: UnitType.BAILIFF,
        name: 'Bailiff',
        role: 'Village subjugation',
        unitClass: UnitClass.SPECIAL,
        attack: 30,
        defInfantry: 100,
        defCavalry: 100,
        defArchers: 100,
        speed: 35,
        carryCapacity: 0,
        // Twenty-five each, so a conquest party is a hundred population standing
        // idle in your farm. That weight is the real cost of taking land, and it
        // is what stops a village changing hands on a spare afternoon.
        population: 25,
        // Four of these take a village from full, so this is a quarter of the
        // price of a conquest.
        cost: { wood: 800, clay: 800, iron: 900, grain: 300 },
        trainingTimeSeconds: 3600,
        trainedAt: 'manor_house',
        requires: { manor_house: 15 },
    },
    knight: {
        type: UnitType.KNIGHT,
        name: 'Knight',
        role: 'Elite heavy infantry',
        /*
         * INFANTRY, not cavalry: the Knight fights dismounted, in full plate, with
         * a greatsword. The class decides which of a defender's three defence
         * stats applies, so while this was CAVALRY a spear wall met him with its
         * anti-cavalry value (45 on a Spearman) instead of its anti-infantry one
         * (15). A hedge of spears stops a horse; it does not stop a man on foot.
         */
        unitClass: UnitClass.INFANTRY,
        /*
         * Tuned to be population-efficient rather than resource-efficient, which
         * is what makes it worth its gate. At the old numbers Heavy Cavalry beat
         * it on value per resource AND per population, leaving the most expensive
         * unlock in the game with no dimension it actually won: only speed, carry
         * and its matrix row. Now the two split cleanly. Per 100 resources Heavy
         * Cavalry is still ahead; per population point the Knight is, and
         * population is the binding constraint late, because Farm levels are the
         * steepest curve a village climbs.
         */
        attack: 220,
        defInfantry: 280,
        defCavalry: 230,
        defArchers: 250,
        speed: 10,
        carryCapacity: 100,
        population: 4,
        cost: { wood: 220, clay: 160, iron: 320, grain: 120 },
        trainingTimeSeconds: 400,
        /*
         * Barracks, not Stable. The Knight was cavalry once and kept its Stable
         * gate when it was reclassified as infantry, which left the game training
         * a man who fights on foot in a paddock. The Stable requirement moved to
         * the Barracks at the same depth, so the unlock is no easier, just
         * coherent: plate and a greatsword are drilled in the yard.
         */
        trainedAt: 'barracks',
        requires: { barracks: 20, smithy: 10, manor_house: 20 },
    },
    cannon: {
        type: UnitType.CANNON,
        name: 'Cannon',
        role: 'Heavy gun - siege and coastal defence',
        /*
         * The Smithy 20 unlock the design brief always named but never defined.
         *
         * Deliberately slow and expensive enough that it is a decision rather than
         * a staple: it out-damages a catapult and is the only thing on land that
         * can hurt a fleet, but at eight population and thirty minutes a gun it
         * cannot be the backbone of an army.
         */
        unitClass: UnitClass.SIEGE,
        attack: 210,
        defInfantry: 140,
        defCavalry: 70,
        defArchers: 140,
        speed: 36,
        carryCapacity: 0,
        population: 8,
        cost: { wood: 500, clay: 250, iron: 800, grain: 100 },
        trainingTimeSeconds: 1800,
        trainedAt: 'smithy',
        requires: { smithy: 20 },
    },
    cog: {
        type: UnitType.COG,
        name: 'Cog',
        role: 'Transport - carries an army over water',
        /*
         * The workhorse. Without hulls no army leaves the mainland, so this is
         * cheap and carries a lot, and is nearly helpless in a fight: a fleet of
         * loaded cogs with no escort is an invitation.
         */
        unitClass: UnitClass.NAVAL,
        attack: 20,
        defInfantry: 120,
        defCavalry: 120,
        defArchers: 120,
        speed: 24,
        carryCapacity: 200,
        population: 6,
        transport: 20,
        cost: { wood: 400, clay: 100, iron: 60, grain: 40 },
        trainingTimeSeconds: 900,
        trainedAt: 'dockyard',
        requires: { dockyard: 1 },
    },
    longship: {
        type: UnitType.LONGSHIP,
        name: 'Longship',
        role: 'Raider - fast, carries a little, fights',
        /*
         * The middle option, and the one that makes coastal raiding viable without
         * committing a fleet: fast enough to be somewhere else by the time a
         * response is mustered, and able to carry the handful of men a raid needs.
         */
        unitClass: UnitClass.NAVAL,
        attack: 90,
        defInfantry: 100,
        defCavalry: 100,
        defArchers: 100,
        speed: 18,
        carryCapacity: 120,
        population: 5,
        transport: 8,
        cost: { wood: 350, clay: 80, iron: 120, grain: 40 },
        trainingTimeSeconds: 1200,
        trainedAt: 'dockyard',
        requires: { dockyard: 5 },
    },
    war_galley: {
        type: UnitType.WAR_GALLEY,
        name: 'War Galley',
        role: 'Warship - sinks hulls, carries nothing',
        /*
         * Carries no cargo at all, which is the point: a galley exists to sink the
         * transports behind it. Drowning an army in its cogs is far cheaper than
         * beating that army on the beach.
         */
        unitClass: UnitClass.NAVAL,
        attack: 190,
        defInfantry: 220,
        defCavalry: 220,
        defArchers: 220,
        speed: 30,
        carryCapacity: 60,
        population: 10,
        transport: 0,
        cost: { wood: 700, clay: 200, iron: 400, grain: 100 },
        trainingTimeSeconds: 2400,
        trainedAt: 'dockyard',
        requires: { dockyard: 12, smithy: 5 },
    },
};
export function getUnit(type) {
    const def = UNITS[type];
    if (!def)
        throw new Error(`Unknown unit type: ${type}`);
    return def;
}
/** The defence stat that applies when `attackerClass` comes at you. */
export function defenceStatFor(def, attackerClass) {
    switch (attackerClass) {
        case UnitClass.CAVALRY:
            return def.defCavalry;
        case UnitClass.ARCHER:
            return def.defArchers;
        // Siege engines and Bailiffs advance on foot and are met by the
        // anti-infantry line.
        case UnitClass.INFANTRY:
        case UnitClass.SIEGE:
        case UnitClass.SPECIAL:
        // A hull carries one defence value written into all three stats, so it
        // does not matter which line asks.
        case UnitClass.NAVAL:
        default:
            return def.defInfantry;
    }
}
export const UNITS_BY_BUILDING = {
    barracks: [
        UnitType.SPEARMAN,
        UnitType.SWORDSMAN,
        UnitType.AXEMAN,
        UnitType.MILITIA_ARCHER,
        UnitType.ARCHER,
        UnitType.CROSSBOWMAN,
        UnitType.KNIGHT,
    ],
    stable: [UnitType.SCOUT, UnitType.LIGHT_CAVALRY, UnitType.HEAVY_CAVALRY],
    smithy: [UnitType.RAM, UnitType.CATAPULT, UnitType.CANNON],
    manor_house: [UnitType.BAILIFF],
    dockyard: [UnitType.COG, UnitType.LONGSHIP, UnitType.WAR_GALLEY],
};
//# sourceMappingURL=units.js.map